/**
 * Collection page interactivity:
 *
 *   <collection-filter-drawer> — bottom-sheet drawer on mobile, static sidebar
 *     on desktop. Native focus trap via Tab handler; Escape closes; clicking
 *     the close button or the backdrop closes. Returns focus to the trigger.
 *
 *   [data-collection-sort-select] — sort <select>, auto-submits its form on
 *     change. Works on all viewports.
 *
 *   [data-collection-filters] — filter form, auto-submits on change on desktop
 *     only (≥1024px), with a small debounce so quickly-toggled checkboxes
 *     coalesce into one reload. Mobile relies on the explicit Apply button to
 *     avoid accidental reloads while customers explore options.
 */

const DESKTOP_MQ = '(min-width: 1024px)';

class CollectionFilterDrawer extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-trigger]');
    this.drawer = this.querySelector('[data-drawer]');
    this.closeBtn = this.querySelector('[data-close]');
    this.backdrop = this.querySelector('[data-backdrop]');
    this.mq = window.matchMedia(DESKTOP_MQ);

    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.open());
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    // If the viewport crosses to desktop while open, just close — the drawer
    // becomes a static sidebar there and the open state is meaningless.
    this.mq.addEventListener('change', () => {
      if (this.mq.matches && this.isOpen) this.close();
    });
  }

  get isOpen() {
    return this.drawer && this.drawer.dataset.open === 'true';
  }

  open() {
    if (!this.drawer) return;
    this.drawer.dataset.open = 'true';
    this.previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    if (this.closeBtn) this.closeBtn.focus();
    this._trap = (e) => this._handleTrap(e);
    this.drawer.addEventListener('keydown', this._trap);
  }

  close() {
    if (!this.drawer) return;
    this.drawer.dataset.open = 'false';
    document.body.style.overflow = '';
    if (this._trap) {
      this.drawer.removeEventListener('keydown', this._trap);
      this._trap = null;
    }
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  _handleTrap(e) {
    if (e.key !== 'Tab') return;
    const focusables = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('collection-filter-drawer')) {
  customElements.define('collection-filter-drawer', CollectionFilterDrawer);
}

// Sort dropdown — auto-submit on change.
document.querySelectorAll('[data-collection-sort-select]').forEach((sel) => {
  sel.addEventListener('change', () => {
    if (sel.form) sel.form.submit();
  });
});

// Filter form — auto-submit on change, desktop only, debounced so a customer
// toggling several boxes in a row gets one reload instead of N.
document.querySelectorAll('[data-collection-filters]').forEach((form) => {
  const mq = window.matchMedia(DESKTOP_MQ);
  let timer = null;
  form.addEventListener('change', () => {
    if (!mq.matches) return;
    clearTimeout(timer);
    timer = setTimeout(() => form.submit(), 300);
  });
});

// View density toggle — comfortable / compact, persisted in localStorage.
// The pre-paint <script> in layout/theme.liquid already set the html
// data-grid-density attribute (if a preference exists) before first paint,
// so the only thing this needs to do at startup is reconcile the grid's
// class modifier and the buttons' aria-pressed with the saved preference.
(function () {
  const STORAGE_KEY = 'coral-grid-density';
  const grid = document.querySelector('.main-collection__grid');
  const buttons = document.querySelectorAll('[data-view-toggle]');
  if (!grid || !buttons.length) return;

  function read() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }
  function write(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* Private browsing / storage disabled — silently skip persistence. */
    }
  }

  function apply(density) {
    const isCompact = density === 'compact';
    grid.classList.toggle('main-collection__grid--compact', isCompact);
    grid.classList.toggle('main-collection__grid--comfortable', !isCompact);
    buttons.forEach((btn) => {
      btn.setAttribute(
        'aria-pressed',
        btn.dataset.viewToggle === density ? 'true' : 'false'
      );
    });
    if (isCompact) {
      document.documentElement.dataset.gridDensity = 'compact';
    } else {
      delete document.documentElement.dataset.gridDensity;
    }
  }

  const stored = read();
  apply(stored === 'compact' ? 'compact' : 'comfortable');

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const density = btn.dataset.viewToggle;
      apply(density);
      write(density);
    });
  });
})();
